import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../../hooks/useAuth'
import { supabase } from '../../../config/supabase'
import { getOrCreateRSVPInvite, updateRSVPInvite } from '../../../services/rsvpInviteService'
import { showAlert, showConfirm } from '../../../services/dialogService'
import { DEFAULT_RSVP_QUESTIONS, nextCustomQuestionId } from '../../../constants/rsvpFormQuestions'
import imageCompression from 'browser-image-compression'
import InfoButton from '../../../components/InfoButton/InfoButton'
import Loader from '../../../components/Loader/Loader'
import TextareaWithCounter from '../../../components/TextareaWithCounter/TextareaWithCounter'
import '../Section.css'
import './CraftYourInvite.css'

function CraftYourInvite() {
  const { user, loading: authLoading } = useAuth()
  const [invite, setInvite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [copied, setCopied] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const autoSaveTimeoutRef = useRef(null)
  const MAX_MESSAGE_LENGTH = 500 // Límite de caracteres para el mensaje

  const [formData, setFormData] = useState({
    partner1_name: '',
    partner2_name: '',
    wedding_date: '',
    wedding_venue: '',
    title: '',
    message: '',
    image_url: '',
    web_enabled: true,
    share_enabled: false,
  })

  // Preguntas del formulario RSVP (predeterminadas o personalizadas)
  const [formQuestions, setFormQuestions] = useState([])

  useEffect(() => {
    // Cleanup del timeout cuando el componente se desmonte
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!user) return

    const loadInvite = async () => {
      try {
        setLoading(true)
        
        const inviteData = await getOrCreateRSVPInvite(user.id)
        setInvite(inviteData)
        
        // IMPORTANTE: Usar los valores guardados en inviteData primero
        // Solo usar valores por defecto si no existen en la base de datos
        // Esto asegura que los cambios guardados por el usuario se muestren correctamente
        
        // Obtener la fecha de la boda desde weddings solo si no hay fecha guardada en invite
        let finalWeddingDate = inviteData.wedding_date || ''
        if (!finalWeddingDate) {
          try {
            const { data: weddingData } = await supabase
              .from('weddings')
              .select('event_date')
              .eq('user_id', user.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle()

            if (weddingData?.event_date) {
              finalWeddingDate = weddingData.event_date
              // Solo actualizar en la base de datos si realmente no había fecha guardada
              await updateRSVPInvite(inviteData.id, { wedding_date: finalWeddingDate })
            }
          } catch (err) {
            console.warn('Could not fetch wedding date:', err)
          }
        }
        
        // Cargar TODOS los valores guardados desde la base de datos
        // IMPORTANTE: Usar valores guardados tal como están (incluso si son null o string vacío)
        // Solo usar valores por defecto si el campo no existe en absoluto (undefined)
        setFormData({
          partner1_name: inviteData.partner1_name !== undefined && inviteData.partner1_name !== null ? inviteData.partner1_name : '',
          partner2_name: inviteData.partner2_name !== undefined && inviteData.partner2_name !== null ? inviteData.partner2_name : '',
          wedding_date: finalWeddingDate,
          wedding_venue: inviteData.wedding_venue !== undefined && inviteData.wedding_venue !== null ? inviteData.wedding_venue : '',
          title: inviteData.title !== undefined && inviteData.title !== null ? inviteData.title : '',
          message: inviteData.message !== undefined && inviteData.message !== null ? inviteData.message : '',
          image_url: inviteData.image_url !== undefined && inviteData.image_url !== null ? inviteData.image_url : '',
          web_enabled: inviteData.web_enabled !== undefined ? inviteData.web_enabled : true,
          share_enabled: inviteData.share_enabled === true,
        })
        
        if (inviteData.image_url) {
          setImagePreview(inviteData.image_url)
        }

        // Preguntas RSVP: usar guardadas o predeterminadas
        const raw = inviteData.form_questions
        if (raw && Array.isArray(raw) && raw.length > 0) {
          setFormQuestions(raw.map((q) => ({ ...q })))
        } else {
          setFormQuestions(DEFAULT_RSVP_QUESTIONS.map((q) => ({ ...q })))
        }

        // Limpiar imágenes huérfanas en Storage: dejar solo la que usa esta invitación
        if (user?.id) {
          try {
            const { data: fileList, error: listError } = await supabase.storage
              .from('rsvp_assets')
              .list(user.id, { limit: 100 })
            if (!listError && fileList?.length > 0) {
              const currentUrl = inviteData.image_url || ''
              const currentPathMatch = currentUrl.match(/\/rsvp_assets\/(.+?)(?:\?|$)/)
              const currentPath = currentPathMatch ? decodeURIComponent(currentPathMatch[1]) : ''
              const currentFileName = currentPath.includes('/') ? currentPath.split('/').pop() : currentPath
              const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif']
              const toDelete = fileList
                .filter((f) => imageExtensions.some((ext) => (f.name || '').toLowerCase().endsWith(ext)))
                .filter((f) => !currentFileName || f.name !== currentFileName)
                .map((f) => `${user.id}/${f.name}`)
              if (toDelete.length > 0) {
                await supabase.storage.from('rsvp_assets').remove(toDelete)
              }
            }
          } catch (e) {
            console.warn('Cleanup RSVP storage:', e)
          }
        }
      } catch (error) {
        console.error('Error loading invite:', error)
      } finally {
        setTimeout(() => {
          setLoading(false)
        }, 150)
      }
    }

    loadInvite()
  }, [user])

  const handleInputChange = async (e) => {
    const { name, value, type, checked } = e.target
    const newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    }
    setFormData(newFormData)
    
    // Guardar automáticamente los cambios después de un pequeño delay
    if (invite) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
      autoSaveTimeoutRef.current = setTimeout(() => {
        handleAutoSave(newFormData)
      }, 1000) // Guardar después de 1 segundo de inactividad
    }
  }

  const handleAutoSave = async (dataToSave) => {
    if (!invite || saving) {
      console.log('⏸️ Auto-save cancelado:', { invite: !!invite, saving })
      return
    }

    try {
      console.log('💾 Auto-save iniciado...')
      console.log('💾 dataToSave:', dataToSave)
      
      // IMPORTANTE: Usar el valor que el usuario ingresó (dataToSave.wedding_date)
      // NO sobrescribir con weddings.event_date, ya que el usuario puede querer una fecha diferente
      // Solo usar weddings.event_date como fallback si el usuario no ingresó nada
      let weddingDate = dataToSave.wedding_date || null
      
      // Si el usuario no ingresó fecha, intentar obtenerla de weddings como fallback
      if (!weddingDate) {
        try {
          const { data: weddingData } = await supabase
            .from('weddings')
            .select('event_date')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle()

          if (weddingData?.event_date) {
            weddingDate = weddingData.event_date
          }
        } catch (err) {
          console.warn('Could not fetch wedding date for auto-save:', err)
        }
      }
      
      // Guardar exactamente lo que el usuario ingresó/modificó
      // IMPORTANTE: Preservar strings vacíos ('') tal como están, no convertirlos a null
      const updates = {
        partner1_name: dataToSave.partner1_name !== undefined ? dataToSave.partner1_name : '',
        partner2_name: dataToSave.partner2_name !== undefined ? dataToSave.partner2_name : '',
        wedding_date: weddingDate || null,
        wedding_venue: dataToSave.wedding_venue !== undefined ? dataToSave.wedding_venue : '',
        title: dataToSave.title !== undefined ? dataToSave.title : '',
        message: dataToSave.message !== undefined ? dataToSave.message : '',
        image_url: imagePreview || dataToSave.image_url || null,
        web_enabled: dataToSave.web_enabled !== undefined ? dataToSave.web_enabled : true,
        share_enabled: dataToSave.share_enabled === true,
      }
      
      console.log('💾 updates a guardar:', updates)
      console.log('💾 invite.id:', invite.id)
      
      const updated = await updateRSVPInvite(invite.id, updates)
      
      console.log('💾 updated recibido:', updated)
      
      if (updated) {
        setInvite(updated)
        
        // Actualizar formData con los valores guardados desde la base de datos para mantener sincronización
        // IMPORTANTE: Usar los valores devueltos por la BD, no los valores locales
        setFormData({
          partner1_name: updated.partner1_name !== undefined && updated.partner1_name !== null ? updated.partner1_name : '',
          partner2_name: updated.partner2_name !== undefined && updated.partner2_name !== null ? updated.partner2_name : '',
          wedding_date: updated.wedding_date || '',
          wedding_venue: updated.wedding_venue !== undefined && updated.wedding_venue !== null ? updated.wedding_venue : '',
          title: updated.title !== undefined && updated.title !== null ? updated.title : '',
          message: updated.message !== undefined && updated.message !== null ? updated.message : '',
          image_url: updated.image_url || '',
          web_enabled: updated.web_enabled !== undefined ? updated.web_enabled : true,
          share_enabled: updated.share_enabled === true,
        })
        
        console.log('✅ Auto-save completado exitosamente')
      } else {
        console.warn('⚠️ Auto-save: updated es null o undefined')
      }
      
      // No mostrar alerta para auto-save
    } catch (error) {
      console.error('❌ Error auto-saving invite:', error)
      console.error('❌ Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
      })
      // No mostrar alerta para auto-save, solo loguear el error
    }
  }

  const handleRemoveImage = async () => {
    if (!invite || (!imagePreview && !formData.image_url)) return
    const ok = await showConfirm('Remove the invitation image? Guests will see the invite without a photo.', { title: 'Remove image', confirmLabel: 'Remove', cancelLabel: 'Cancel', variant: 'danger' })
    if (!ok) return
    const imageUrl = imagePreview || formData.image_url
    try {
      if (imageUrl && imageUrl.includes('rsvp_assets')) {
        const pathMatch = imageUrl.match(/\/rsvp_assets\/(.+?)(?:\?|$)/)
        if (pathMatch && pathMatch[1]) {
          const filePath = decodeURIComponent(pathMatch[1])
          const { error: deleteError } = await supabase.storage
            .from('rsvp_assets')
            .remove([filePath])
          if (deleteError) {
            console.warn('Could not delete image from storage:', deleteError)
          }
        }
      }
      setImagePreview(null)
      setFormData((prev) => ({ ...prev, image_url: '' }))
      const updated = await updateRSVPInvite(invite.id, { image_url: null })
      if (updated) setInvite(updated)
    } catch (err) {
      console.error('Error removing image:', err)
      await showAlert('Could not remove image. Please try again.')
    }
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (!file || !user || !invite) return

    try {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        await showAlert('Please select an image file')
        return
      }

      // Validar tamaño (máximo 10MB antes de comprimir)
      if (file.size > 10 * 1024 * 1024) {
        await showAlert('Image size must be less than 10MB')
        return
      }

      // Eliminar TODAS las imágenes anteriores del usuario en rsvp_assets
      try {
        // Listar todas las imágenes del usuario en el bucket rsvp_assets
        const { data: fileList, error: listError } = await supabase.storage
          .from('rsvp_assets')
          .list(user.id, {
            limit: 100,
            offset: 0,
            sortBy: { column: 'created_at', order: 'desc' }
          })

        if (listError) {
          console.warn('Could not list previous images:', listError)
          // Continuar aunque falle la lista
        } else if (fileList && fileList.length > 0) {
          // Construir los paths completos de todas las imágenes
          const filesToDelete = fileList
            .filter(file => {
              // Solo eliminar archivos de imagen (jpg, jpeg, png, webp, etc.)
              const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif']
              return imageExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
            })
            .map(file => `${user.id}/${file.name}`)

          if (filesToDelete.length > 0) {
            console.log(`Eliminando ${filesToDelete.length} imagen(es) anterior(es)...`)
            
            // Eliminar todas las imágenes anteriores
            const { error: deleteError } = await supabase.storage
              .from('rsvp_assets')
              .remove(filesToDelete)

            if (deleteError) {
              console.warn('Could not delete previous images:', deleteError)
              // No bloquear el proceso si falla la eliminación
            } else {
              console.log(`✅ ${filesToDelete.length} imagen(es) eliminada(s) correctamente`)
            }
          }
        }
      } catch (deleteErr) {
        console.warn('Error deleting previous images:', deleteErr)
        // Continuar con la subida aunque falle la eliminación
      }

      // Mostrar preview inmediato del archivo original
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)

      // Comprimir la imagen antes de subirla
      let compressedFile = file
      
      try {
        const options = {
          maxSizeMB: 1, // Máximo 1MB después de comprimir (más grande que avatar porque son imágenes de invitación)
          maxWidthOrHeight: 1920, // Redimensionar a máximo 1920px (mantiene buena calidad)
          useWebWorker: true,
          fileType: file.type,
          initialQuality: 0.85 // Calidad inicial del 85% (buen balance calidad/tamaño)
        }

        const compressed = await imageCompression(file, options)
        
        // Verificar que el archivo comprimido sea válido
        if (compressed && compressed.size > 0) {
          compressedFile = compressed
        } else {
          console.warn('Archivo comprimido inválido, usando original')
          compressedFile = file
        }
      } catch (compressionError) {
        console.warn('Error comprimiendo imagen, usando original:', compressionError)
        compressedFile = file
      }

      // Subir imagen comprimida a Supabase Storage
      const fileExt = 'jpg' // Siempre usar .jpg después de comprimir
      const fileName = `rsvp-${invite.rsvp_slug}-${Date.now()}.${fileExt}`
      const filePath = `${user.id}/${fileName}`

      // Asegurar que el tipo de contenido sea correcto
      const contentType = compressedFile.type || 'image/jpeg'

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('rsvp_assets')
        .upload(filePath, compressedFile, {
          upsert: true,
          cacheControl: '3600',
          contentType: contentType
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        if (uploadError.message?.includes('Bucket not found') || uploadError.message?.includes('does not exist')) {
          await showAlert('Error: The "rsvp_assets" storage bucket does not exist. Please create it in Supabase Storage and make it public.')
        } else if (uploadError.message?.includes('row-level security')) {
          await showAlert('Error: Storage policies not configured. Please run RSVP_STORAGE_POLICIES.sql in Supabase.')
        } else {
          await showAlert(`Error uploading image: ${uploadError.message || 'Please try again.'}`)
        }
        return
      }

      // Obtener URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('rsvp_assets')
        .getPublicUrl(filePath)

      if (!publicUrl) {
        await showAlert('Error generating image URL. Please try again.')
        return
      }

      // Actualizar preview con la URL pública (imagen comprimida)
      setImagePreview(publicUrl)
      setFormData(prev => ({ ...prev, image_url: publicUrl }))

      // Guardar automáticamente la URL en la base de datos
      const updated = await updateRSVPInvite(invite.id, { image_url: publicUrl })
      if (updated) {
        setInvite(updated) // Actualizar el estado de la invitación
      }

      await showAlert(`Image uploaded successfully! Saved ${((1 - compressedFile.size / file.size) * 100).toFixed(1)}% of space.`)
    } catch (error) {
      console.error('Error uploading image:', error)
      await showAlert('Error uploading image. Please try again.')
    }
  }

  const handleSave = async () => {
    if (!invite) return

    try {
      setSaving(true)
      
      // IMPORTANTE: Usar el valor que el usuario ingresó (formData.wedding_date)
      // NO sobrescribir con weddings.event_date, ya que el usuario puede querer una fecha diferente
      // Solo usar weddings.event_date como fallback si el usuario no ingresó nada
      let weddingDate = formData.wedding_date || null
      
      // Si el usuario no ingresó fecha, intentar obtenerla de weddings como fallback
      if (!weddingDate) {
        try {
          const { data: weddingData } = await supabase
            .from('weddings')
            .select('event_date')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle()

          if (weddingData?.event_date) {
            weddingDate = weddingData.event_date
          }
        } catch (err) {
          console.warn('Could not fetch wedding date for save:', err)
        }
      }
      
      // Guardar exactamente lo que el usuario ingresó/modificó
      const sanitizedFormQuestions = formQuestions.map((q) => {
        if (!Array.isArray(q.options)) return { ...q }
        const options = q.options.map((o) => (typeof o === 'string' ? o.trim() : '')).filter(Boolean)
        return { ...q, options }
      })
      const updates = {
        partner1_name: formData.partner1_name !== undefined ? formData.partner1_name : '',
        partner2_name: formData.partner2_name !== undefined ? formData.partner2_name : '',
        wedding_date: weddingDate || null,
        wedding_venue: formData.wedding_venue !== undefined ? formData.wedding_venue : '',
        title: formData.title !== undefined ? formData.title : '',
        message: formData.message !== undefined ? formData.message : '',
        image_url: imagePreview || formData.image_url || null,
        web_enabled: formData.web_enabled !== undefined ? formData.web_enabled : true,
        share_enabled: formData.share_enabled === true,
        form_questions: sanitizedFormQuestions.length ? sanitizedFormQuestions : null,
      }
      const updated = await updateRSVPInvite(invite.id, updates)
      setInvite(updated) // Actualizar el estado con la invitación actualizada
      
      // Actualizar formData con los valores guardados desde la base de datos para mantener sincronización
      // IMPORTANTE: Usar los valores devueltos por la BD, no los valores locales
      if (updated) {
        setFormData({
          partner1_name: updated.partner1_name !== undefined && updated.partner1_name !== null ? updated.partner1_name : '',
          partner2_name: updated.partner2_name !== undefined && updated.partner2_name !== null ? updated.partner2_name : '',
          wedding_date: updated.wedding_date || '',
          wedding_venue: updated.wedding_venue !== undefined && updated.wedding_venue !== null ? updated.wedding_venue : '',
          title: updated.title !== undefined && updated.title !== null ? updated.title : '',
          message: updated.message !== undefined && updated.message !== null ? updated.message : '',
          image_url: updated.image_url || '',
          web_enabled: updated.web_enabled !== undefined ? updated.web_enabled : true,
          share_enabled: updated.share_enabled === true,
        })
        if (updated.form_questions && Array.isArray(updated.form_questions)) {
          setFormQuestions(updated.form_questions.map((q) => ({ ...q })))
        }
      }
      
      await showAlert('Invitation saved successfully!')
    } catch (error) {
      console.error('Error saving invite:', error)
      await showAlert('Error saving invitation. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const copyLink = () => {
    if (!invite) return
    const link = `${window.location.origin}/rsvp/${invite.rsvp_slug}`
    navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
    } catch {
      return dateString
    }
  }

  const isCustomQuestion = (q) => (q.id || '').toString().startsWith('custom-')

  const updateFormQuestion = (index, field, value) => {
    setFormQuestions((prev) => {
      const next = [...prev]
      if (!next[index]) return prev
      next[index] = { ...next[index], [field]: value }
      return next
    })
  }

  const removeFormQuestion = (index) => {
    setFormQuestions((prev) => prev.filter((_, i) => i !== index))
  }

  const moveFormQuestionUp = (index) => {
    if (index <= 0) return
    setFormQuestions((prev) => {
      const next = [...prev]
      ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
      return next
    })
  }

  const moveFormQuestionDown = (index) => {
    if (index >= formQuestions.length - 1) return
    setFormQuestions((prev) => {
      const next = [...prev]
      ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
      return next
    })
  }

  const addCustomQuestion = (type = 'custom_text') => {
    const id = nextCustomQuestionId(formQuestions)
    const newQ = type === 'custom_choice'
      ? { id, type: 'custom_choice', columnLabel: '', label: '', options: ['Option 1', 'Option 2', 'Option 3'], required: false, multiple: false }
      : type === 'custom_textarea'
        ? { id, type: 'custom_textarea', columnLabel: '', label: '', placeholder: '', required: false }
        : { id, type: 'custom_text', columnLabel: '', label: '', placeholder: '', required: false }
    setFormQuestions((prev) => [...prev, newQ])
  }

  const restoreDefaultQuestions = async () => {
    const ok = await showConfirm('Restore default RSVP questions? This will replace your current list.', { title: 'Restore defaults', confirmLabel: 'Restore', cancelLabel: 'Cancel' })
    if (ok) {
      setFormQuestions(DEFAULT_RSVP_QUESTIONS.map((q) => ({ ...q })))
    }
  }

  if (authLoading || loading) {
    return <Loader />
  }

  if (!invite) {
    return (
      <div className="dashboard-section">
        <h1>Craft Your Invite</h1>
        <p>Error loading invitation. Please try again.</p>
      </div>
    )
  }

  const rsvpLink = `${window.location.origin}/rsvp/${invite.rsvp_slug}`
  const coupleNames = formData.partner2_name 
    ? `${formData.partner1_name} & ${formData.partner2_name}`
    : formData.partner1_name

  return (
    <div className="dashboard-section craft-invite">
      <h1>
        CRAFT YOUR INVITE
        <InfoButton
          title="Craft Your Invite"
          content={
            <div>
              <p>
                We've kept it simple—an invitation format with just the info your guests actually need. 
                Fill in the blanks, drop in a photo, and we'll take care of linking your RSVP form. 
                Send it out by link or email, and boom—you're ready to start collecting those "yes, we'll be there!" replies.
              </p>
            </div>
          }
        />
      </h1>

      <div className="craft-invite-layout">
        {/* Left Panel - Form */}
        <div className="craft-invite-form-panel">
          <div className="craft-invite-form">
            <div className="craft-form-group">
              <label>YOUR NAME</label>
              <input
                type="text"
                name="partner1_name"
                value={formData.partner1_name}
                onChange={handleInputChange}
                placeholder="Your name"
              />
            </div>

            <div className="craft-form-group">
              <label>MY PARTNER</label>
              <input
                type="text"
                name="partner2_name"
                value={formData.partner2_name}
                onChange={handleInputChange}
                placeholder="Partner name"
              />
            </div>

            <div className="craft-form-group">
              <label>WEDDING DATE</label>
              <input
                type="date"
                name="wedding_date"
                value={formData.wedding_date}
                readOnly
                disabled
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.7)', 
                  cursor: 'not-allowed',
                  opacity: 0.8
                }}
                title="Wedding date is set from your wedding profile and cannot be changed here"
              />
            </div>

            <div className="craft-form-group">
              <label>WEDDING VENUE</label>
              <input
                type="text"
                name="wedding_venue"
                value={formData.wedding_venue}
                onChange={handleInputChange}
                placeholder="Wedding venue"
              />
            </div>

            <div className="craft-form-group">
              <label>TITLE</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Invitation title"
              />
            </div>

            <div className="craft-form-group">
              <label>MESSAGE</label>
              <TextareaWithCounter
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                maxLength={MAX_MESSAGE_LENGTH}
                placeholder="Invitation message"
                rows={4}
                className={formData.message?.length >= MAX_MESSAGE_LENGTH ? 'craft-textarea-max' : ''}
              />
              {formData.message?.length >= MAX_MESSAGE_LENGTH && (
                <span className="craft-char-limit-warning">
                  Maximum character limit reached
                </span>
              )}
            </div>

            <div className="craft-form-group">
              <label>WEB</label>
              <div className="craft-toggle-container">
                <span>{rsvpLink}</span>
                <label className="craft-toggle">
                  <input
                    type="checkbox"
                    name="web_enabled"
                    checked={formData.web_enabled}
                    onChange={handleInputChange}
                  />
                  <span className="craft-toggle-slider"></span>
                </label>
              </div>
            </div>

            <div className="craft-form-group">
              <label>SHARE</label>
              <div className="craft-toggle-container">
                <span>Enable sharing</span>
                <label className="craft-toggle">
                  <input
                    type="checkbox"
                    name="share_enabled"
                    checked={formData.share_enabled}
                    onChange={handleInputChange}
                  />
                  <span className="craft-toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Center Panel - Preview */}
        <div className="craft-invite-preview-panel">
          <div className="craft-preview-card">
            {imagePreview || formData.image_url ? (
              <div className="craft-preview-image">
                <img src={imagePreview || formData.image_url} alt="Wedding" />
                <div className="craft-preview-image-actions">
                  <button
                    type="button"
                    className="craft-change-image-btn"
                    onClick={() => document.getElementById('image-upload').click()}
                    title="Change image"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="craft-remove-image-btn"
                    onClick={handleRemoveImage}
                    title="Remove image"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </div>
            ) : (
              <div className="craft-preview-image-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <button 
                  className="craft-upload-btn"
                  onClick={() => document.getElementById('image-upload').click()}
                >
                  UPLOAD IMAGE
                </button>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </div>
            )}

            <div className="craft-preview-content">
              {formData.title && (
                <div className="craft-preview-title-small">{formData.title}</div>
              )}
              <div className="craft-preview-names">{coupleNames}</div>
              {formData.wedding_date && (
                <div className="craft-preview-date">{formatDate(formData.wedding_date)}</div>
              )}
              {formData.wedding_venue && (
                <div className="craft-preview-venue">{formData.wedding_venue}</div>
              )}
              {formData.message && (
                <div className="craft-preview-message">{formData.message}</div>
              )}
              {/* El enlace es el RSVP; no mostramos "Visit our website" para no confundir */}
            </div>
          </div>
        </div>

        {/* Right Panel - Instructions */}
        <div className="craft-invite-instructions-panel">
          <div className="craft-instruction-item">
            <div className="craft-instruction-arrow">→</div>
            <div className="craft-instruction-text">
              <strong>Photo</strong> - whether it's the two of you, a detail from your wedding style, or something that just feels right.
            </div>
          </div>

          <div className="craft-instruction-item">
            <div className="craft-instruction-arrow">→</div>
            <div className="craft-instruction-text">
              <strong>Names, date & place</strong> - the essentials, front and centre.
            </div>
          </div>

          <div className="craft-instruction-item">
            <div className="craft-instruction-arrow">→</div>
            <div className="craft-instruction-text">
              <strong>Title</strong> - think of it as the opening line of your story. A small sentence that sets the mood and gets everyone excited.
            </div>
          </div>

          <div className="craft-instruction-item">
            <div className="craft-instruction-arrow">→</div>
            <div className="craft-instruction-text">
              <strong>Message</strong> - your chance to tell guests why this day matters and why you'd love them there. Keep it warm, keep it you.
            </div>
          </div>
        </div>
      </div>

      {/* RSVP form questions */}
      <div className="craft-rsvp-questions-section">
        <h3 className="craft-rsvp-questions-title">RSVP form questions</h3>
        <p className="craft-rsvp-questions-intro">
          Choose which questions guests see and edit the text. Add your own questions or use the defaults.
        </p>
        <div className="craft-rsvp-questions-toolbar">
          <button type="button" className="craft-rsvp-btn craft-rsvp-btn-add" onClick={() => addCustomQuestion('custom_text')}>
            + Add question (short text)
          </button>
          <button type="button" className="craft-rsvp-btn craft-rsvp-btn-add" onClick={() => addCustomQuestion('custom_textarea')}>
            + Add question (long text)
          </button>
          <button type="button" className="craft-rsvp-btn craft-rsvp-btn-add" onClick={() => addCustomQuestion('custom_choice')}>
            + Add question (multiple choice)
          </button>
          <button type="button" className="craft-rsvp-btn craft-rsvp-btn-restore" onClick={restoreDefaultQuestions}>
            Restore defaults
          </button>
        </div>
        <ul className="craft-rsvp-questions-list">
          {formQuestions.map((q, index) => (
            <li key={q.id} className="craft-rsvp-question-item">
              <div className="craft-rsvp-question-left">
                <span className="craft-rsvp-question-num">{index + 1}.</span>
                <div className="craft-rsvp-question-move">
                  <button
                    type="button"
                    className="craft-rsvp-btn-move"
                    onClick={() => moveFormQuestionUp(index)}
                    disabled={index === 0}
                    title="Move up"
                    aria-label="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="craft-rsvp-btn-move"
                    onClick={() => moveFormQuestionDown(index)}
                    disabled={index === formQuestions.length - 1}
                    title="Move down"
                    aria-label="Move down"
                  >
                    ↓
                  </button>
                </div>
              </div>
              <div className="craft-rsvp-question-fields">
                {isCustomQuestion(q) && (
                  <input
                    type="text"
                    className="craft-rsvp-question-column-label-input"
                    value={q.columnLabel ?? ''}
                    onChange={(e) => updateFormQuestion(index, 'columnLabel', e.target.value)}
                    placeholder="Column name in Guest List (e.g. Dietary)"
                  />
                )}
                <input
                  type="text"
                  className="craft-rsvp-question-label-input"
                  value={q.label || ''}
                  onChange={(e) => updateFormQuestion(index, 'label', e.target.value)}
                  placeholder="Question text"
                />
                {q.type === 'dietary' && (
                  <>
                    <TextareaWithCounter
                      className="craft-rsvp-question-options-textarea"
                      value={Array.isArray(q.options) ? q.options.join('\n') : (q.options || '')}
                      onChange={(e) => updateFormQuestion(index, 'options', e.target.value.split(/\r?\n/))}
                      placeholder="One option per line (e.g. None, Vegetarian, Vegan, Celiac...)"
                      rows={4}
                      maxLength={500}
                    />
                    <label className="craft-rsvp-multiple-label">
                      <input
                        type="checkbox"
                        checked={!!q.multiple}
                        onChange={(e) => updateFormQuestion(index, 'multiple', e.target.checked)}
                      />
                      Allow multiple selections
                    </label>
                    <span className="craft-rsvp-question-hint">One option per line. You can use commas and spaces in each option.</span>
                  </>
                )}
                {q.type === 'custom_choice' && (
                  <>
                    <TextareaWithCounter
                      className="craft-rsvp-question-options-textarea"
                      value={Array.isArray(q.options) ? q.options.join('\n') : (q.options || '')}
                      onChange={(e) => updateFormQuestion(index, 'options', e.target.value.split(/\r?\n/))}
                      placeholder="One option per line. You can use commas and spaces in each option."
                      rows={4}
                      maxLength={500}
                    />
                    <label className="craft-rsvp-multiple-label">
                      <input
                        type="checkbox"
                        checked={!!q.multiple}
                        onChange={(e) => updateFormQuestion(index, 'multiple', e.target.checked)}
                      />
                      Allow multiple selections
                    </label>
                    <span className="craft-rsvp-question-hint">One option per line. Commas and spaces are allowed in each option.</span>
                  </>
                )}
                {(q.type === 'allergies' || q.type === 'notes' || q.type === 'custom_text' || q.type === 'custom_textarea') && (
                  <input
                    type="text"
                    className="craft-rsvp-question-placeholder-input"
                    value={q.placeholder || ''}
                    onChange={(e) => updateFormQuestion(index, 'placeholder', e.target.value)}
                    placeholder="Placeholder (optional)"
                  />
                )}
              </div>
              <div className="craft-rsvp-question-meta">
                <span className="craft-rsvp-question-type">{q.type.replace('_', ' ')}</span>
                {!isCustomQuestion(q) && <span className="craft-rsvp-question-default-badge">default</span>}
                <button type="button" className="craft-rsvp-btn-remove" onClick={() => removeFormQuestion(index)} title="Remove question">
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
        {formQuestions.length === 0 && (
          <p className="craft-rsvp-questions-empty">No questions. Add one or restore defaults.</p>
        )}
      </div>

      <div className="craft-invite-actions">
        <button 
          className="craft-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Make it shine'}
        </button>
        {invite && (
          <button 
            className="craft-copy-link-btn"
            onClick={copyLink}
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        )}
      </div>
    </div>
  )
}

export default CraftYourInvite
